import * as React from "react";
import KeyboardUtils from "sh-application/utility/KeyboardUtils";

/**
 * Custom hook for handling keyboard navigation between shift blocks.
 * Supports arrow key navigation (up/down/left/right) through focusable elements.
 *
 * @param addShiftBlockButtonId - ID of the add shift block button to focus on after deletion
 * @returns Object containing keyboard event handler and deletion handler
 */
export const useShiftBlockKeyboardNavigation = (addShiftBlockButtonId: string) => {
    const handleKeyboardNavigation = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        // Check if the event originated from an interactive child component that needs its own arrow key navigation
        const eventTarget = e.target as HTMLElement;
        const isFromInteractiveChild = eventTarget.closest('[role="combobox"]') !== null || // Dropdown
                                      eventTarget.closest('button') !== null || // Buttons
                                      eventTarget.closest('[role="checkbox"]') !== null || // Checkboxes
                                      eventTarget.closest('input') !== null || // Input fields
                                      eventTarget.closest('select') !== null; // Select elements

        // If the event is from an interactive child, don't interfere - let the child handle it
        if (isFromInteractiveChild) {
            // Stop the event from bubbling up to prevent parent components from handling it
            e.stopPropagation();
            return;
        }

        // Navigate between shift blocks using arrow keys
        if (KeyboardUtils.isDownPressed(e) || KeyboardUtils.isRightPressed(e)) {
            const container = e.currentTarget;
            const focusableElements = Array.from(container.querySelectorAll('[tabindex="0"]')) as HTMLElement[];
            const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
            if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
                e.preventDefault();
                focusableElements[currentIndex + 1].focus();
            }
        } else if (KeyboardUtils.isUpPressed(e) || KeyboardUtils.isLeftPressed(e)) {
            const container = e.currentTarget;
            const focusableElements = Array.from(container.querySelectorAll('[tabindex="0"]')) as HTMLElement[];
            const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
            if (currentIndex > 0) {
                e.preventDefault();
                focusableElements[currentIndex - 1].focus();
            }
        }
    }, []);

    const handleShiftBlockDeleted = React.useCallback(() => {
        // Focus on the Add Shift Block button after a shift block is deleted
        document.getElementById(addShiftBlockButtonId)?.focus();
    }, [addShiftBlockButtonId]);

    return {
        handleKeyboardNavigation,
        handleShiftBlockDeleted
    };
};
